import { Flex } from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { FormikPersist } from '@components/FormikPersist';
import { NavBar } from '@components/NavBar';
import { ScrollToError } from '@components/ScrollToError';
import { UpdatingModal } from '@components/UpdatingModal';
import { INCOME_PROTECTOR } from '@constants/formNames';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { RemoveDialog } from '@features/RemoveDialog';
import { ProductSubtype, ProductType } from '@generated/gql/graphql';
import { IncomeProtectorInsuranceQuote } from '@src/types/insurance';
import { getQuoteMonthlyPrice } from '@utils/price';

import { Declarations } from './Declarations';
import { IncomeProtectorFAQ } from './FAQ';
import { IncomeProtectorHero } from './Hero';
import { PolicyDocuments } from './PolicyDocuments';
import { Protection } from './Protection';
import { useIncomeProtectorHandler } from './useIncomeProtectorHandler';
import { IPLegalSectionValues, validationSchema } from './validationSchema';

const initialValues: IPLegalSectionValues = {
  declarations: {
    age: false,
    existingCondition: false,
    working: false,
    myself: false,
  },
};

interface IncomeProtectorContentProps {
  basketId: string;
  insuranceQuote: IncomeProtectorInsuranceQuote;
  onSelectCompleted?: () => void;
  onDeselectCompleted?: () => void;
}

const IncomeProtectorContent: FC<IncomeProtectorContentProps> = ({
  basketId,
  insuranceQuote,
  onSelectCompleted,
  onDeselectCompleted,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const updatingDialog = useDialog(DialogID.UPDATING_BUNDLE);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const addToBundle = (event: MouseEvent) => {
    event.preventDefault();
    formRef?.current?.requestSubmit();
    return;
  };

  const { onSubmit, isLoading, isRemoving, removeInsurance } =
    useIncomeProtectorHandler(
      insuranceQuote,
      basketId,
      onSelectCompleted,
      onDeselectCompleted
    );

  useEffect(() => {
    updatingDialog.setOpen(isLoading);
  }, [isLoading, updatingDialog]);

  return (
    <>
      <div className="wrapper">
        <Flex direction="column" spacing="2xl" paddingBottom="600">
          <IncomeProtectorHero price={getQuoteMonthlyPrice(insuranceQuote)} />
          <Protection />
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <>
                <FormSync />
                <FormikPersist name={INCOME_PROTECTOR} />
                <form ref={formRef} onSubmit={handleSubmit}>
                  <Flex direction="column" spacing="2xl">
                    <PolicyDocuments
                      documents={insuranceQuote.product.details?.documents}
                    />
                    <Declarations />
                    <ScrollToError orderedFields={['declarations']} />
                  </Flex>
                </form>
              </>
            )}
          </Formik>
        </Flex>
      </div>
      <IncomeProtectorFAQ />
      <RemoveDialog
        isOpen={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onRemove={removeInsurance}
        productType={ProductType.Insurance}
        productName={insuranceQuote.product.name}
      />
      <NavBar
        nextLabel={insuranceQuote.selected ? 'Save' : 'Add to bundle'}
        onNext={addToBundle}
        nextDisabled={isLoading}
        hasRemoveButton={insuranceQuote.selected}
        onRemove={() => setRemoveDialogOpen(true)}
      />
      <UpdatingModal
        heading="Updating your bundle..."
        productType={ProductType.Insurance}
        subType={ProductSubtype.IncomeProtector}
        isRemoving={isRemoving}
      />
    </>
  );
};

export { IncomeProtectorContent };
